import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { PlusIcon, Square2StackIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { Widget } from "@/utils/widgets";
import useTabStore from "@/stores/useTabStore";
import { usePortalContainer } from "@/shared/context/PortalContext/usePortalContext";

/** Fresh per-add composite id, mirroring WidgetsDropdownMenu's handleAddWidget. */
const makeCompositeId = (widgetId: string) =>
  `${widgetId}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Header dropdown that copies a widget onto another tab — or a brand-new one —
 * without going through the Manage Widgets panel. Reuses the tab store's
 * addTab/addWidget actions (addWidget computes the grid position itself).
 */
export function AddToTabMenu({ widget }: { widget: Widget }) {
  // Headless UI panels portal to the main window's <body>, so this would render
  // in the wrong window from a popped-out widget. Managing tab layout from a
  // popout isn't a target flow either — hide it there (matches FilterComponents).
  const isPopout = usePortalContainer() !== null;

  const tabs = useTabStore((s) => s.tabs);
  const activeTabId = useTabStore((s) => s.activeTabId);
  const addTab = useTabStore((s) => s.addTab);
  const addWidget = useTabStore((s) => s.addWidget);

  if (isPopout) return null;

  const otherTabs = tabs.filter((tab) => tab.id !== activeTabId);

  const handleAddToTab = (tabId: string, tabTitle: string) => {
    addWidget(tabId, makeCompositeId(widget.id));
    toast.success(`Added ${widget.name} to ${tabTitle}`);
  };

  const handleNewTab = () => {
    const title = prompt("Enter new tab title");
    if (!title) return;
    addTab(title); // creates the tab and switches activeTabId to it
    addWidget(useTabStore.getState().activeTabId, makeCompositeId(widget.id));
  };

  return (
    <Menu>
      <MenuButton
        className="p-1 no-drag bg-transparent hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-blue-600"
        title="Add to a tab"
      >
        <Square2StackIcon className="text-gray-700 dark:text-white h-3.5 w-3.5" />
      </MenuButton>
      <MenuItems
        transition
        anchor={{ to: "bottom end", gap: 4 }}
        className="z-50 w-48 rounded-md border border-gray-200 bg-white text-gray-700 shadow-xl ring-1 ring-black/5 transition duration-100 ease-out focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 dark:border-gray-700/50 dark:bg-gray-800 dark:text-gray-200 dark:ring-white/10"
      >
        <div className="px-3 py-1.5 text-2xs font-semibold uppercase tracking-wide text-gray-400">
          Add to tab
        </div>

        {otherTabs.length === 0 ? (
          <div className="px-3 py-1.5 text-xs italic text-gray-400">
            No other tabs
          </div>
        ) : (
          otherTabs.map((tab) => (
            <MenuItem key={tab.id}>
              <button
                onClick={() => handleAddToTab(tab.id, tab.title)}
                className="block w-full truncate bg-transparent px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-100 data-[focus]:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700 dark:data-[focus]:bg-gray-700"
              >
                {tab.title}
              </button>
            </MenuItem>
          ))
        )}

        <div className="my-1 border-t border-gray-200 dark:border-gray-700" />

        <MenuItem>
          <button
            onClick={handleNewTab}
            className="flex w-full items-center gap-1.5 bg-transparent px-3 py-1.5 text-left text-xs text-squawk-blue hover:bg-gray-100 data-[focus]:bg-gray-100 dark:text-squawk-blue-light dark:hover:bg-gray-700 dark:data-[focus]:bg-gray-700"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            New tab…
          </button>
        </MenuItem>
      </MenuItems>
    </Menu>
  );
}
